import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { FiUploadCloud } from "react-icons/fi";
import { MdSortByAlpha } from "react-icons/md";
import { IoFilterSharp } from "react-icons/io5";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { canvasImageStore } from "../../stores/ImageStore";
import "../styles/CustomUploadPanel.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import UploadResponseModal from "../Account/UploadResponseModal";
import loadingSpinner from "../../assets/img/Spinner.svg";
import editorStore from "../../stores/EditorStore";
import ImagesGrid, { ImageItem } from "../../Components/ImagesGrid";

const MAX_FILE_SIZE = 200 * 1024 * 1024;

const C = {
  surface:  '#1c1e2e',
  surface2: '#252840',
  border:   'rgba(255,255,255,0.07)',
  text1:    '#f0f2ff',
  text2:    '#9da3c8',
  text3:    '#5c6290',
  accent:   '#5b79ff',
  accentSoft: 'rgba(91,121,255,0.15)',
  menuBg:   '#1c1e2e',
  menuHover:'rgba(91,121,255,0.1)',
};

const menuItemStyle = (hovered: boolean): React.CSSProperties => ({
  display: 'block', padding: '8px 14px',
  fontSize: 12, color: hovered ? C.text1 : C.text2,
  background: hovered ? C.menuHover : 'none',
  cursor: 'pointer', border: 'none', width: '100%', textAlign: 'left',
  transition: 'all 120ms',
});

const CustomUploadPanel: React.FC = observer(() => {
  const [sortOption, setSortOption]     = useState<string | null>(null);
  const [filterOption, setFilterOption] = useState<string | null>(null);
  const [isUploading, setIsUploading]   = useState(false);
  const [isModalOpen, setModalOpen]     = useState(false);

  useEffect(() => {
    loadImages();
  }, [sortOption, filterOption]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadImages = async () => {
    try { await canvasImageStore.retrievePhotos("canvas", sortOption || "", filterOption || ""); }
    catch (error) { console.error("Error loading images:", error); }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    setIsUploading(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size exceeds the maximum limit of 200MB.", { position: "top-right" });
        setIsUploading(false);
        return;
      }
      const validTypes = ["image/jpg", "image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        toast.error(`File type ${file.type} is not allowed.`, { position: "top-right" });
        setIsUploading(false);
        return;
      }
      formData.append("files", file);
    }
    try {
      await canvasImageStore.uploadImages(formData, true);
      if (canvasImageStore.error) {
        toast.error(canvasImageStore.error, { position: "top-right" });
      } else {
        setModalOpen(true);
        await canvasImageStore.retrievePhotos("canvas", sortOption || "", filterOption || "");
      }
    } catch {
      toast.error("Failed to upload image.", { position: "top-right" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImage = async (id: number) => {
    try {
      setIsUploading(true);
      await canvasImageStore.deleteImage(id);
      toast.success("Image deleted successfully!", { position: "top-right" });
    } catch {
      toast.error(canvasImageStore.error || "Failed to delete image.", { position: "top-right" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelect = (image: ImageItem) => {
    localStorage.setItem("uploadId", String(image.id));
    const img = new Image();
    img.src = image.src;
    img.onload = () => {
      const scale = Math.min(editorStore.width / img.width, editorStore.height / img.height);
      const finalW = img.width * scale;
      const finalH = img.height * scale;
      editorStore.activePage.addElement({
        type: "image", src: image.src,
        width: finalW, height: finalH,
        x: (editorStore.width - finalW) / 2,
        y: (editorStore.height - finalH) / 2,
      });
    };
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
    minWidth: 160, zIndex: 100,
    background: C.menuBg,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    overflow: 'hidden',
    padding: '4px 0',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      {/* Header toolbar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: C.surface,
        borderBottom: `1px solid ${C.border}`,
        padding: '10px 12px',
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4,
      }}>
        {/* Upload */}
        <label htmlFor="canvas-image-upload" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          cursor: 'pointer', padding: '6px 4px', borderRadius: 8,
          transition: 'background 150ms',
        }}
          onMouseEnter={e => (e.currentTarget.style.background = C.accentSoft)}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          <FiUploadCloud style={{ fontSize: 18, color: C.accent }} />
          <span style={{ fontSize: 10, color: C.text2, fontWeight: 500 }}>Upload</span>
          <input type="file" accept="image/jpg,image/jpeg,image/png,image/gif" multiple
            id="canvas-image-upload" onChange={handleImageUpload} style={{ display: "none" }} />
        </label>

        {/* Sort */}
        <Menu as="div" style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
          <MenuButton style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            cursor: 'pointer', padding: '6px 4px', borderRadius: 8,
            background: 'none', border: 'none', width: '100%',
            transition: 'background 150ms',
          }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <MdSortByAlpha style={{ fontSize: 18, color: C.text2 }} />
            <span style={{ fontSize: 10, color: C.text2, fontWeight: 500 }}>Sort</span>
          </MenuButton>
          <MenuItems style={dropdownStyle}>
            {[["newest","Newest First"],["oldest","Oldest First"],["a-z","A → Z"],["z-a","Z → A"]].map(([val, label]) => (
              <MenuItem key={val}>
                <button style={menuItemStyle(false)}
                  onMouseEnter={e => { e.currentTarget.style.background = C.menuHover; e.currentTarget.style.color = C.text1; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.text2; }}
                  onClick={() => setSortOption(val)}>
                  {label}
                </button>
              </MenuItem>
            ))}
          </MenuItems>
        </Menu>

        {/* Filter */}
        <Menu as="div" style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
          <MenuButton style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            cursor: 'pointer', padding: '6px 4px', borderRadius: 8,
            background: 'none', border: 'none', width: '100%',
            transition: 'background 150ms',
          }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <IoFilterSharp style={{ fontSize: 18, color: C.text2 }} />
            <span style={{ fontSize: 10, color: C.text2, fontWeight: 500 }}>Filter</span>
          </MenuButton>
          <MenuItems style={dropdownStyle}>
            {[["all","All Photos"],["hide-used","Hide Used"],["show-used","Used Only"]].map(([val, label]) => (
              <MenuItem key={val}>
                <button style={menuItemStyle(false)}
                  onMouseEnter={e => { e.currentTarget.style.background = C.menuHover; e.currentTarget.style.color = C.text1; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.text2; }}
                  onClick={() => setFilterOption(val)}>
                  {label}
                </button>
              </MenuItem>
            ))}
          </MenuItems>
        </Menu>
      </div>

      {isUploading && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(13,15,26,0.75)',
        }}>
          <img src={loadingSpinner} alt="Loading..." style={{ width: 64, height: 64 }} />
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <ImagesGrid
          images={canvasImageStore.images}
          onSelect={handleSelect}
          onDelete={handleDeleteImage}
          isLoading={canvasImageStore.isLoading}
          itemHeight={100}
        />
      </div>

      <UploadResponseModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
});

export default CustomUploadPanel;
